import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useData } from "@/context/DataContext";
import TaskRow from "@/components/TaskRow";
import TaskModal from "@/components/TaskModal";
import ClientModal from "@/components/ClientModal";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Plus,
  ChevronDown,
  ChevronRight,
  CalendarClock,
  FileText,
  Save,
  Camera,
  Trash2,
  ArrowUpDown,
  Search,
  Paperclip,
  CheckSquare,
  Copy,
  CalendarDays,
  X,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { initials, formatDate, isOverdue } from "@/lib/tp";
import { toast } from "sonner";
import { fileToResizedDataUrl } from "@/lib/image";
import { ADMIN_SECTION_CLIENT_ID } from "@/lib/roles";

function toDateFromISO(s) {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function toISOFromDate(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function ClientAvatar({ client }) {
  const { updateClient } = useData();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 4MB)");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToResizedDataUrl(file, 320, 0.85);
      await updateClient(client.id, { avatar_url: dataUrl });
      toast.success("Immagine aggiornata");
    } catch (err) {
      toast.error("Errore caricamento immagine");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <button
        onClick={onPick}
        data-testid={`client-avatar-${client.id}`}
        title="Cambia immagine"
        className="relative h-11 w-11 rounded-2xl overflow-hidden shrink-0 shadow-lg group focus:outline-none focus:ring-2 focus:ring-fuchsia-500/60"
        style={{
          backgroundColor: client.avatar_url ? "transparent" : (client.color || "#0A84FF"),
          backgroundImage: client.avatar_url ? `url(${client.avatar_url})` : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {!client.avatar_url && (
          <span className="flex items-center justify-center h-full w-full text-white font-bold">
            {initials(client.name)}
          </span>
        )}
        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/45 transition-colors opacity-0 group-hover:opacity-100">
          <Camera size={16} className="text-white" />
        </span>
        {uploading && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="h-4 w-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          </span>
        )}
      </button>
      <input
        ref={inputRef}
        data-testid={`client-avatar-input-${client.id}`}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
    </>
  );
}

function ContractBadge({ client }) {
  const { updateClient } = useData();
  const expiry = client.contract_expiry;
  const overdue = expiry && isOverdue(expiry);
  const dateObj = toDateFromISO(expiry);

  const save = async (d) => {
    await updateClient(client.id, { contract_expiry: toISOFromDate(d) });
    toast.success("Scadenza aggiornata");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`client-expiry-${client.id}`}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider border transition-colors ${
            overdue
              ? "bg-red-500/15 border-red-500/30 text-red-300"
              : expiry
                ? "bg-white/5 border-white/10 text-zinc-200 hover:bg-white/10"
                : "bg-white/[0.03] border-white/10 text-zinc-500 hover:text-white hover:bg-white/10"
          }`}
        >
          <CalendarClock size={12} />
          <span className="opacity-70 hidden sm:inline">Scadenza:</span>
          <span>{expiry ? formatDate(expiry) : "—"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="tp-glass-strong border-white/10 p-0 text-white w-auto" align="start">
        <Calendar mode="single" selected={dateObj} onSelect={save} initialFocus />
        {expiry && (
          <div className="p-2 border-t border-white/10">
            <button
              onClick={() => save(null)}
              className="w-full text-xs text-zinc-400 hover:text-white py-1.5"
            >
              Rimuovi scadenza
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function AnagraficaSection({ client, prominent = false, defaultOpen = false }) {
  const { updateClient } = useData();
  const [open, setOpen] = useState(defaultOpen);
  const [notes, setNotes] = useState(client.notes || "");
  const [attachments, setAttachments] = useState(client.attachments || []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // Edit toggle: se ci sono note salvate → vista di sola lettura con link cliccabili.
  // Cliccando "Modifica" (o se le note sono vuote) → editor.
  const [editing, setEditing] = useState(!(client.notes || "").trim());
  const fileRef = useRef(null);

  useEffect(() => {
    setNotes(client.notes || "");
    setAttachments(client.attachments || []);
    setDirty(false);
    setEditing(!(client.notes || "").trim());
  }, [client.notes, client.attachments]);

  const save = async () => {
    setSaving(true);
    try {
      await updateClient(client.id, { notes, attachments });
      setDirty(false);
      setEditing(false);
      toast.success("Anagrafica salvata");
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const addFiles = async (files) => {
    if (!files || files.length === 0) return;
    try {
      const items = await Promise.all(
        files.map(
          (f) =>
            new Promise((res, rej) => {
              if (f.size > 5 * 1024 * 1024) return rej(new Error(`${f.name}: > 5MB`));
              const r = new FileReader();
              r.onload = () =>
                res({
                  id: `att_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                  name: f.name,
                  type: f.type,
                  size: f.size,
                  data_url: r.result,
                });
              r.onerror = () => rej(r.error);
              r.readAsDataURL(f);
            }),
        ),
      );
      setAttachments((p) => [...p, ...items]);
      setDirty(true);
    } catch (err) {
      toast.error(err.message || "Errore file");
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) {
      setEditing(true);
      await addFiles(files);
    }
  };

  const removeAttachment = (id) => {
    setAttachments((p) => p.filter((a) => a.id !== id));
    setDirty(true);
  };

  return (
    <div
      className={
        prominent
          ? "tp-glass rounded-3xl overflow-hidden border border-fuchsia-400/20 shadow-lg shadow-fuchsia-600/10"
          : "mx-3 sm:mx-4 mb-3 rounded-2xl border border-white/5 bg-white/[0.02]"
      }
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`anagrafica-toggle-${client.id}`}
        className={`w-full flex items-center gap-2 text-left hover:bg-white/[0.03] transition-colors ${
          prominent ? "px-5 sm:px-6 py-4 rounded-t-3xl" : "px-4 py-2.5 rounded-2xl"
        }`}
      >
        {open ? (
          <ChevronDown size={prominent ? 16 : 14} className={prominent ? "text-fuchsia-300" : "text-zinc-400"} />
        ) : (
          <ChevronRight size={prominent ? 16 : 14} className={prominent ? "text-fuchsia-300" : "text-zinc-400"} />
        )}
        <FileText size={prominent ? 16 : 14} className={prominent ? "text-fuchsia-400" : "text-zinc-400"} />
        <span
          className={
            prominent
              ? "text-base font-semibold text-white"
              : "text-xs uppercase tracking-widest font-semibold text-zinc-300"
          }
        >
          Anagrafica
        </span>
        {notes && !open && (
          <span className={`ml-2 truncate ${prominent ? "text-xs text-zinc-400" : "text-[11px] text-zinc-500"}`}>
            · {notes.slice(0, 60)}
            {notes.length > 60 ? "…" : ""}
          </span>
        )}
      </button>
      {open && (
        <div
          className={`px-4 pb-3 space-y-2 tp-fade-in rounded-b-2xl transition-colors ${
            dragOver ? "ring-2 ring-fuchsia-400/60 bg-fuchsia-950/10" : ""
          }`}
          data-testid={`anagrafica-dropzone-${client.id}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
          onDrop={onDrop}
        >
          {editing ? (
            <Textarea
              data-testid={`anagrafica-textarea-${client.id}`}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setDirty(true);
              }}
              placeholder="Contatti, referente, note contrattuali, indirizzo di fatturazione, ecc… i link diventano cliccabili al salvataggio"
              className={`bg-white/5 border-white/10 text-white text-sm leading-relaxed ${
                prominent ? "min-h-[180px]" : "min-h-[110px]"
              }`}
            />
          ) : (
            <div className="space-y-2" data-testid={`anagrafica-view-${client.id}`}>
              {notes && notes.match(/(https?:\/\/|www\.)/i) ? (
                <div
                  data-testid={`anagrafica-preview-${client.id}`}
                  className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words"
                >
                  {notes
                    .split(/(\s+)/)
                    .map((tok, i) => {
                      const isHttp = /^https?:\/\/\S+$/i.test(tok);
                      const isWww = /^www\.\S+$/i.test(tok);
                      if (isHttp || isWww) {
                        const href = isHttp ? tok : `https://${tok}`;
                        return (
                          <a key={i} href={href} target="_blank" rel="noreferrer" className="text-fuchsia-300 hover:text-fuchsia-200 underline break-all">
                            {tok}
                          </a>
                        );
                      }
                      return <span key={i}>{tok}</span>;
                    })}
                </div>
              ) : (
                <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words">
                  {notes || <span className="italic text-zinc-500">— nessuna nota —</span>}
                </div>
              )}
              <div className="flex justify-end">
                <button
                  type="button"
                  data-testid={`anagrafica-edit-btn-${client.id}`}
                  onClick={() => setEditing(true)}
                  className="text-[11px] px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white border border-white/10"
                >
                  Modifica
                </button>
              </div>
            </div>
          )}
          {editing && (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  await addFiles(files);
                }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid={`anagrafica-attach-btn-${client.id}`}
                className="h-8 px-3 rounded-lg border border-dashed border-white/15 text-[11px] text-zinc-400 hover:text-white hover:border-fuchsia-400/50 hover:bg-white/[0.03] transition-colors inline-flex items-center gap-1.5"
              >
                <Paperclip size={12} /> Aggiungi file · trascina qui
              </button>
              {attachments.length > 0 && (
                <span className="text-[10px] text-zinc-500 tabular-nums">
                  {attachments.length} allegato/i
                </span>
              )}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid={`anagrafica-attachments-${client.id}`}>
              {attachments.map((a) => {
                const isImg = (a.type || "").startsWith("image/");
                return (
                  <div
                    key={a.id}
                    className="group relative rounded-lg border border-white/10 bg-white/[0.02] overflow-hidden"
                    style={{ width: isImg ? 64 : "auto" }}
                  >
                    {isImg ? (
                      <a href={a.data_url} target="_blank" rel="noreferrer">
                        <img src={a.data_url} alt={a.name} className="h-16 w-16 object-cover" />
                      </a>
                    ) : (
                      <a
                        href={a.data_url}
                        download={a.name}
                        className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] text-zinc-300 hover:text-white"
                        title={a.name}
                      >
                        <FileText size={12} />
                        <span className="max-w-[120px] truncate">{a.name}</span>
                      </a>
                    )}
                    <button
                      onClick={() => removeAttachment(a.id)}
                      className="absolute top-0 right-0 h-4 w-4 bg-black/70 text-white text-[10px] rounded-bl-md opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Rimuovi"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {editing && (
            <div className="flex items-center justify-end gap-2">
              {dirty && (
                <span className="text-[11px] text-amber-300">Modifiche non salvate</span>
              )}
              <Button
                onClick={save}
                disabled={saving || !dirty}
                data-testid={`anagrafica-save-${client.id}`}
                className="tp-brand-btn h-8 rounded-lg text-xs px-3"
              >
                <Save size={12} className="mr-1.5" />
                {saving ? "Salvo..." : "Salva"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CredenzialiSection({ client }) {
  const { updateClient } = useData();
  const [open, setOpen] = useState(false);
  const [creds, setCreds] = useState(client.credentials || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCreds(client.credentials || "");
    setDirty(false);
  }, [client.credentials]);

  const save = async () => {
    setSaving(true);
    try {
      await updateClient(client.id, { credentials: creds });
      setDirty(false);
      toast.success("Credenziali salvate");
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-3 sm:mx-4 mb-3 rounded-2xl border border-white/5 bg-white/[0.02]">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`credenziali-toggle-${client.id}`}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors rounded-2xl"
      >
        {open ? (
          <ChevronDown size={14} className="text-zinc-400" />
        ) : (
          <ChevronRight size={14} className="text-zinc-400" />
        )}
        <FileText size={14} className="text-zinc-400" />
        <span className="text-xs uppercase tracking-widest font-semibold text-zinc-300">
          Credenziali
        </span>
        {creds && !open && (
          <span className="ml-2 text-[11px] text-zinc-500 truncate">· ••••••••</span>
        )}
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-2 tp-fade-in">
          <Textarea
            data-testid={`credenziali-textarea-${client.id}`}
            value={creds}
            onChange={(e) => {
              setCreds(e.target.value);
              setDirty(true);
            }}
            placeholder="Utente, password, chiavi API, note di accesso, ecc…"
            className="bg-white/5 border-white/10 text-white min-h-[110px] text-sm leading-relaxed font-mono"
          />
          <div className="flex items-center justify-end gap-2">
            {dirty && (
              <span className="text-[11px] text-amber-300">Modifiche non salvate</span>
            )}
            <Button
              onClick={save}
              disabled={saving || !dirty}
              data-testid={`credenziali-save-${client.id}`}
              className="tp-brand-btn h-8 rounded-lg text-xs px-3"
            >
              <Save size={12} className="mr-1.5" />
              {saving ? "Salvo..." : "Salva"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function ClientCard({ client, forceExpand = false, archiveMode = false, onChanged }) {
  const { tasks, deleteClient, updateClient, addTask, updateTask, reload } = useData();
  const [expanded, setExpanded] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [moveCalOpen, setMoveCalOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const exitSelect = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  useEffect(() => {
    if (forceExpand) setExpanded(true);
  }, [forceExpand]);

  const clientTasks = useMemo(
    () => tasks.filter((t) => t.client_id === client.id && t.status !== "completato"),
    [tasks, client.id],
  );
  const allClientTasks = useMemo(
    () => tasks.filter((t) => t.client_id === client.id),
    [tasks, client.id],
  );
  const completed = allClientTasks.filter((t) => t.status === "completato").length;

  const removeClient = async () => {
    if (!confirm(`Eliminare ${client.name}? Anche le sue task saranno rimosse.`)) return;
    await deleteClient(client.id);
    toast.success("Cliente eliminato");
  };

  const archiveClient = async (e) => {
    e.stopPropagation();
    if (!confirm(`Archiviare ${client.name}? Sparirà dai clienti principali ma task e credenziali restano salvate.`)) return;
    await updateClient(client.id, { archived: true });
    await reload?.();
    onChanged?.();
    toast.success("Cliente archiviato");
  };

  const restoreClient = async (e) => {
    e.stopPropagation();
    await updateClient(client.id, { archived: false });
    await reload?.();
    onChanged?.();
    toast.success("Cliente ripristinato");
  };

  const selectedTasks = useMemo(
    () => clientTasks.filter((t) => selectedIds.has(t.id)),
    [clientTasks, selectedIds],
  );
  const selCount = selectedTasks.length;

  const bulkDuplicate = async () => {
    if (!selCount || bulkBusy) return;
    setBulkBusy(true);
    try {
      for (const t of selectedTasks) {
        await addTask({
          title: t.title, description: t.description || "", client_id: t.client_id,
          assignee_ids: t.assignee_ids || [], due_date: t.due_date, priority: t.priority,
          status: "da_fare", notify_user_id: t.notify_user_id || null,
          checklist: (Array.isArray(t.checklist) ? t.checklist : []).map((c) => ({ ...c, done: false })),
          attachments: t.attachments || [], duplicate_on_complete: !!t.duplicate_on_complete,
        });
      }
      toast.success(`${selCount} task duplicate`);
      exitSelect();
    } catch { toast.error("Errore durante la duplicazione"); }
    finally { setBulkBusy(false); }
  };

  const bulkDelete = async () => {
    if (!selCount || bulkBusy) return;
    setBulkBusy(true);
    try {
      await Promise.all(selectedTasks.map((t) => updateTask(t.id, { trashed: true })));
      toast.success(`${selCount} task spostate nel Cestino`);
      exitSelect();
    } catch { toast.error("Errore durante l'eliminazione"); }
    finally { setBulkBusy(false); }
  };

  const bulkMove = async (d) => {
    if (!d || !selCount || bulkBusy) return;
    const due = toISOFromDate(d);
    setBulkBusy(true);
    try {
      await Promise.all(selectedTasks.map((t) => updateTask(t.id, { due_date: due })));
      toast.success(`${selCount} task spostate al ${formatDate(due)}`);
      setMoveCalOpen(false);
      exitSelect();
    } catch { toast.error("Errore durante lo spostamento"); }
    finally { setBulkBusy(false); }
  };

  return (
    <section
      data-testid={`client-card-${client.id}`}
      className="tp-glass rounded-3xl overflow-hidden tp-hover-lift relative"
      style={{ borderLeft: `4px solid ${client.color}` }}
    >
      <div className="px-5 sm:px-6 py-5 flex items-center gap-4 flex-wrap">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5"
          aria-label="Espandi"
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <ClientAvatar client={client} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3
              className="text-lg font-semibold truncate"
              style={{ color: client.color }}
              data-testid={`client-name-${client.id}`}
            >
              {client.name}
            </h3>
            {/* Color picker swatch */}
            <label
              className="relative h-5 w-5 rounded-md border border-white/20 cursor-pointer overflow-hidden shrink-0"
              style={{ background: client.color }}
              title="Cambia colore"
              data-testid={`client-color-swatch-${client.id}`}
            >
              <input
                type="color"
                value={client.color}
                onChange={(e) => updateClient(client.id, { color: e.target.value })}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </label>
            {client.company && (
              <span className="text-xs text-zinc-500 truncate">· {client.company}</span>
            )}
            <ContractBadge client={client} />
          </div>
          <div className="mt-1 text-xs text-zinc-400">
            <span className="text-white font-medium tabular-nums">{clientTasks.length}</span>{" "}
            attive{completed > 0 ? (
              <>
                {" · "}
                <span className="tabular-nums">{completed}</span> completate
              </>
            ) : null}
          </div>
        </div>

        <Button
          data-testid={`add-task-for-client-${client.id}`}
          onClick={() => setNewTaskOpen(true)}
          className="tp-brand-btn h-9 rounded-full text-xs px-4 shrink-0"
        >
          <Plus size={12} className="mr-1" /> Task
        </Button>
        {allClientTasks.some((t) => t.status !== "completato") && (
          <button
            data-testid={`client-select-toggle-${client.id}`}
            onClick={(e) => { e.stopPropagation(); setExpanded(true); selectMode ? exitSelect() : setSelectMode(true); }}
            title={selectMode ? "Annulla selezione" : "Seleziona task"}
            className={`flex items-center gap-1.5 rounded-full px-3 h-9 text-xs font-medium shrink-0 transition-colors ${
              selectMode ? "bg-fuchsia-500/15 border border-fuchsia-400/40 text-fuchsia-200" : "border border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
            }`}
          >
            <CheckSquare size={13} /> {selectMode ? "Annulla" : "Seleziona"}
          </button>
        )}
        {archiveMode ? (
          <button
            onClick={restoreClient}
            data-testid={`restore-client-${client.id}`}
            title="Ripristina cliente"
            className="flex items-center gap-1.5 rounded-full px-4 h-9 text-xs font-semibold shrink-0 bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25 transition-colors"
          >
            <ArchiveRestore size={13} /> RIPRISTINA CLIENTE
          </button>
        ) : (
          <button
            onClick={archiveClient}
            data-testid={`archive-client-${client.id}`}
            title="Archivia cliente"
            className="p-2 rounded-lg text-zinc-500 hover:text-amber-300 hover:bg-amber-500/10 transition-colors shrink-0"
          >
            <Archive size={14} />
          </button>
        )}
        <button
          onClick={removeClient}
          data-testid={`delete-client-${client.id}`}
          title="Elimina cliente"
          className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {expanded && (
        <>
          <AnagraficaSection client={client} />
          <CredenzialiSection client={client} />
          <div className="px-2 sm:px-3 pb-3">
            {clientTasks.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-zinc-500 italic">
                {completed > 0
                  ? `Tutte le task attive completate. ${completed} nel cestino.`
                  : "Nessuna task per questo cliente ancora."}
              </div>
            ) : (
              <>
                <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_140px_130px_130px_120px_auto] gap-3 px-4 pb-2 text-[10px] uppercase tracking-widest text-zinc-500">
                  <div>Task</div>
                  <div>Team</div>
                  <div>Scadenza</div>
                  <div>Urgente</div>
                  <div>Stato</div>
                  <div />
                </div>
                <div className="rounded-2xl overflow-hidden">
                  {selectMode && selCount > 0 && (
                    createPortal(
                    <div
                      data-testid={`client-bulk-bar-${client.id}`}
                      className="fixed left-1/2 -translate-x-1/2 z-[60] flex flex-wrap items-center justify-center gap-2 rounded-2xl tp-glass-strong border border-fuchsia-400/30 shadow-2xl shadow-black/50 px-3 py-2 w-[calc(100%-1.5rem)] max-w-xl animate-in fade-in slide-in-from-top-2"
                      style={{ top: "calc(env(safe-area-inset-top) + 4.75rem)" }}
                    >
                      <span className="text-sm text-white px-2 tabular-nums">{selCount} selezionate</span>
                      <div className="h-5 w-px bg-white/15" />
                      <Button data-testid={`client-bulk-duplicate-${client.id}`} onClick={bulkDuplicate} disabled={bulkBusy}
                        variant="ghost" className="h-8 rounded-xl text-zinc-100 hover:bg-white/10 text-xs">
                        <Copy size={14} className="mr-1.5" /> Duplica
                      </Button>
                      <Button data-testid={`client-bulk-move-${client.id}`} onClick={() => setMoveCalOpen(true)} disabled={bulkBusy}
                        variant="ghost" className="h-8 rounded-xl text-zinc-100 hover:bg-white/10 text-xs">
                        <CalendarDays size={14} className="mr-1.5" /> Sposta al…
                      </Button>
                      <Button data-testid={`client-bulk-delete-${client.id}`} onClick={bulkDelete} disabled={bulkBusy}
                        variant="ghost" className="h-8 rounded-xl text-red-300 hover:bg-red-500/15 hover:text-red-200 text-xs">
                        <Trash2 size={14} className="mr-1.5" /> Elimina
                      </Button>
                      <button data-testid={`client-bulk-close-${client.id}`} onClick={exitSelect}
                        className="ml-auto p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10"><X size={15} /></button>
                    </div>,
                      document.body,
                    )
                  )}
                  {clientTasks.map((t) => (
                    selectMode ? (
                      <div key={t.id} className={`flex items-center gap-2 rounded-xl ${selectedIds.has(t.id) ? "bg-fuchsia-500/10 ring-1 ring-fuchsia-400/30" : ""}`}>
                        <label className="pl-2 shrink-0 cursor-pointer">
                          <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)}
                            data-testid={`client-select-${t.id}`} className="accent-fuchsia-500 h-5 w-5" />
                        </label>
                        <div className="flex-1 min-w-0"><TaskRow task={t} /></div>
                      </div>
                    ) : (
                      <TaskRow key={t.id} task={t} />
                    )
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      <TaskModal open={newTaskOpen} onOpenChange={setNewTaskOpen} initialClientId={client.id} />

      <Dialog open={moveCalOpen} onOpenChange={setMoveCalOpen}>
        <DialogContent className="tp-glass-strong border-white/10 text-white w-auto max-w-fit" data-testid={`client-move-calendar-${client.id}`}>
          <DialogHeader>
            <DialogTitle className="text-white text-base">Sposta {selCount} task al giorno…</DialogTitle>
          </DialogHeader>
          <Calendar mode="single" onSelect={bulkMove} className="rounded-md" />
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default function Dashboard() {
  const { clients, loading, tasks } = useData();
  const navigate = useNavigate();
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [sortBy, setSortBy] = useState("alpha");
  const [search, setSearch] = useState("");

  const tasksByClient = useMemo(() => {
    const m = new Map();
    for (const t of tasks) {
      if (!m.has(t.client_id)) m.set(t.client_id, []);
      m.get(t.client_id).push(t);
    }
    return m;
  }, [tasks]);

  const taskMatches = (t, q) =>
    (t.title || "").toLowerCase().includes(q) ||
    (t.description || "").toLowerCase().includes(q) ||
    (Array.isArray(t.checklist) ? t.checklist : []).some((it) =>
      (it.text || "").toLowerCase().includes(q),
    );

  const nameMatches = (c, q) =>
    (c.name || "").toLowerCase().includes(q) ||
    (c.company || "").toLowerCase().includes(q) ||
    (c.contact_email || "").toLowerCase().includes(q);

  const clientHasTaskMatch = (c, q) =>
    (tasksByClient.get(c.id) || []).some((t) => taskMatches(t, q));

  // Clienti che hanno matchato SOLO grazie a una task → li espandiamo in automatico.
  const expandByTask = useMemo(() => {
    const q = search.trim().toLowerCase();
    const s = new Set();
    if (!q) return s;
    for (const c of clients) {
      if (!nameMatches(c, q) && clientHasTaskMatch(c, q)) s.add(c.id);
    }
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, search, tasksByClient]);

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = clients.filter((c) => c.id !== ADMIN_SECTION_CLIENT_ID && !c.archived);
    if (q) {
      list = list.filter((c) => nameMatches(c, q) || clientHasTaskMatch(c, q));
    }
    if (sortBy === "expiry-asc") {
      list.sort((a, b) => {
        if (!a.contract_expiry && !b.contract_expiry) return (a.name || "").localeCompare(b.name || "", "it");
        if (!a.contract_expiry) return 1;
        if (!b.contract_expiry) return -1;
        return a.contract_expiry.localeCompare(b.contract_expiry);
      });
    } else if (sortBy === "expiry-desc") {
      list.sort((a, b) => {
        if (!a.contract_expiry && !b.contract_expiry) return (a.name || "").localeCompare(b.name || "", "it");
        if (!a.contract_expiry) return 1;
        if (!b.contract_expiry) return -1;
        return b.contract_expiry.localeCompare(a.contract_expiry);
      });
    } else {
      list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "it", { sensitivity: "base" }));
    }
    return list;
  }, [clients, sortBy, search, tasksByClient]);

  return (
    <div className="space-y-6 tp-fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Panoramica</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white mt-1">
            Clienti{" "}
            <span className="text-zinc-500 font-normal tabular-nums" data-testid="clients-count">
              ({sorted.length})
            </span>
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Ogni cliente ha la sua scheda con task, anagrafica e scadenze.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <input
              data-testid="clients-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca cliente o task..."
              className="h-10 pl-9 pr-3 rounded-md bg-white/5 border border-white/10 text-white text-sm w-56 focus:outline-none focus:border-fuchsia-400/60 placeholder:text-zinc-500"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger
              data-testid="clients-sort-select"
              className="h-10 bg-white/5 border-white/10 text-white w-56"
            >
              <ArrowUpDown size={13} className="text-zinc-400 mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="tp-glass-strong border-white/10 text-white">
              <SelectItem value="alpha">Ordine alfabetico</SelectItem>
              <SelectItem value="expiry-asc">Scadenza · prossima prima</SelectItem>
              <SelectItem value="expiry-desc">Scadenza · lontana prima</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="archive-clients-button"
            onClick={() => navigate("/archivio-clienti")}
            className="h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-white/10"
          >
            <Archive size={14} className="mr-1.5" /> ARCHIVIO CLIENTI
          </Button>
          <Button
            data-testid="add-client-button"
            onClick={() => setAddClientOpen(true)}
            className="tp-brand-btn h-10 rounded-full"
          >
            <Plus size={14} className="mr-1.5" /> Nuovo cliente
          </Button>
        </div>
      </div>

      {loading && <div className="text-zinc-500 text-sm">Caricamento…</div>}

      {!loading && sorted.length === 0 && (
        <div className="tp-glass rounded-3xl p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
            <Building2 className="text-zinc-400" />
          </div>
          <h3 className="text-xl font-semibold text-white">Nessun cliente ancora</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Aggiungi il primo cliente per iniziare a organizzare le task.
          </p>
          <Button
            data-testid="add-client-empty-button"
            onClick={() => setAddClientOpen(true)}
            className="tp-brand-btn mt-5 rounded-full"
          >
            <Plus size={14} className="mr-1.5" /> Crea cliente
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {sorted.map((c) => (
          <ClientCard key={c.id} client={c} forceExpand={expandByTask.has(c.id)} />
        ))}
      </div>

      <ClientModal open={addClientOpen} onOpenChange={setAddClientOpen} />
    </div>
  );
}
